import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { getDeviceId } from '../lib/deviceId'

/** Espera máxima pelo primeiro sync do presence antes de liberar a gravação. */
const PRESENCE_TIMEOUT_MS = 2500

/**
 * useRealtimeReuniao — sincroniza reunião ao vivo entre devices via Supabase Realtime.
 *
 * 3 mecanismos:
 * 1. Postgres Changes: escuta UPDATE em `reunioes` e INSERT em `reuniao_imagens`
 * 2. Presence: tracking de devices conectados, incluindo quem está gravando
 * 3. Broadcast: ownership de notas (e o eco de rec_state, mantido para o header)
 *
 * O estado de gravação vive no **Presence**, não no broadcast. Broadcast é evento:
 * quem entra no canal depois nunca recebe o que já foi enviado, então o segundo
 * aparelho abria sem saber que o primeiro já estava gravando. Presence é estado
 * retido — chega no `sync` inicial de quem acabou de entrar — e some sozinho
 * quando o aparelho desconecta.
 */
export interface ReuniaoDevice {
  userId?: string
  deviceId?: string
  /** Id estável do aparelho (localStorage) — o mesmo que nomeia a pasta no Storage. */
  stableId?: string
  deviceType?: 'mobile' | 'desktop'
  joinedAt?: string
  recording: boolean
}

export function useRealtimeReuniao(reuniaoId: number | string | undefined) {
  const { user } = useAuth()
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)

  // ── State sincronizado ──
  const [devices, setDevices]           = useState<ReuniaoDevice[]>([])
  const [remoteNotas, setRemoteNotas]   = useState(null)
  const [remotePresentes, setRemotePresentes] = useState(null)
  const [remoteStatus, setRemoteStatus] = useState(null)
  const [remoteImagens, setRemoteImagens] = useState([])
  const [remoteRecState, setRemoteRecState] = useState(null)
  const [notasOwner, setNotasOwner]     = useState(null) // userId que está editando notas
  // Vira true no primeiro `sync` do presence — ou por timeout, se o realtime não
  // responder. Quem consome usa isto para esperar antes de gravar automaticamente:
  // sem a espera, o segundo aparelho começaria a gravar antes de saber que o
  // primeiro já está. O timeout garante que realtime fora do ar não impeça gravar.
  const [presenceReady, setPresenceReady] = useState(false)

  // Identidade da ABA no canal: evita reagir às próprias mensagens. Continua por
  // montagem de propósito — duas abas no mesmo navegador precisam se distinguir.
  const myDeviceId = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`)
  // Identidade do APARELHO: estável entre reloads, é a que nomeia a pasta dos
  // segmentos no Storage e a que a UI mostra ("gravando no celular").
  const myStableId = useRef(getDeviceId())
  // Payload atual do presence — `track()` substitui o objeto inteiro, então
  // guardamos o último para não perder campos ao atualizar só `recording`.
  const presencePayload = useRef<Record<string, unknown>>({})

  // ── Detectar tipo de device ──
  const deviceType = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent)
    ? 'mobile' : 'desktop'

  // ── Setup canal ──
  useEffect(() => {
    if (!reuniaoId || !user) return

    const channel = supabase.channel(`reuniao:${reuniaoId}`, {
      config: { presence: { key: myDeviceId.current } },
    })

    // 1. Postgres Changes — reunioes
    channel.on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'reunioes', filter: `id=eq.${reuniaoId}` },
      (payload) => {
        const row = payload.new
        if (row.notas_reuniao !== undefined) setRemoteNotas(row.notas_reuniao)
        if (row.presentes_nomes !== undefined) setRemotePresentes(row.presentes_nomes)
        if (row.status && row.status !== 'AGENDADA') setRemoteStatus(row.status)
      }
    )

    // 2. Postgres Changes — reuniao_imagens (novas fotos)
    channel.on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'reuniao_imagens', filter: `reuniao_id=eq.${reuniaoId}` },
      (payload) => {
        setRemoteImagens(prev => [...prev, payload.new])
      }
    )

    // 3. Broadcast — estado de gravação + ownership de notas
    channel.on('broadcast', { event: 'rec_state' }, ({ payload }) => {
      if (payload.deviceId !== myDeviceId.current) {
        setRemoteRecState(payload.state)
      }
    })

    channel.on('broadcast', { event: 'notas_owner' }, ({ payload }) => {
      if (payload.deviceId !== myDeviceId.current) {
        setNotasOwner(payload.userId || null)
      }
    })

    // 4. Presence — devices conectados (e quem está gravando)
    channel.on('presence', { event: 'sync' }, () => {
      // O tipo do presenceState() do supabase-js só garante `presence_ref`;
      // o resto do payload é o que nós mesmos enviamos no track().
      const state = channel.presenceState() as Record<string, Partial<ReuniaoDevice>[]>
      const devs: ReuniaoDevice[] = Object.values(state).flat().map(p => ({
        userId: p.userId,
        deviceId: p.deviceId,
        stableId: p.stableId,
        deviceType: p.deviceType,
        joinedAt: p.joinedAt,
        recording: !!p.recording,
      }))
      setDevices(devs)
      setPresenceReady(true)
    })

    // Subscribe e registrar presença
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        presencePayload.current = {
          userId: user.id,
          deviceId: myDeviceId.current,
          stableId: myStableId.current,
          deviceType,
          joinedAt: new Date().toISOString(),
          recording: presencePayload.current.recording ?? false,
        }
        await channel.track(presencePayload.current)
      }
    })

    channelRef.current = channel

    // Rede de segurança: realtime indisponível não pode impedir a gravação.
    const readyFallback = setTimeout(() => setPresenceReady(true), PRESENCE_TIMEOUT_MS)

    return () => {
      clearTimeout(readyFallback)
      supabase.removeChannel(channel)
      channelRef.current = null
      setPresenceReady(false)
    }
  }, [reuniaoId, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reserva de gravação (Presence) ──
  /**
   * Publica no Presence que este aparelho está (ou parou de estar) gravando.
   * É o que faz o segundo aparelho não começar a gravar sozinho.
   */
  const setRecordingPresence = useCallback((recording: boolean) => {
    if (!channelRef.current) return
    presencePayload.current = { ...presencePayload.current, recording }
    channelRef.current.track(presencePayload.current)
  }, [])

  /** Outro aparelho (não esta aba) que está gravando esta reunião agora. */
  const gravandoEmOutroDevice = devices.find(d => d.deviceId !== myDeviceId.current && d.recording) || null

  // ── Broadcast helpers ──
  const broadcastRecState = useCallback((state) => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'rec_state',
      payload: { deviceId: myDeviceId.current, state },
    })
  }, [])

  const claimNotasOwnership = useCallback(() => {
    if (!user) return
    setNotasOwner(user.id)
    channelRef.current?.send({
      type: 'broadcast',
      event: 'notas_owner',
      payload: { deviceId: myDeviceId.current, userId: user.id },
    })
  }, [user])

  const releaseNotasOwnership = useCallback(() => {
    setNotasOwner(null)
    channelRef.current?.send({
      type: 'broadcast',
      event: 'notas_owner',
      payload: { deviceId: myDeviceId.current, userId: null },
    })
  }, [])

  // Sou eu o owner das notas?
  const isNotasOwner = notasOwner === null || notasOwner === user?.id

  return {
    devices,
    deviceCount: devices.length,
    remoteNotas,
    remotePresentes,
    remoteStatus,
    remoteImagens,
    remoteRecState,
    notasOwner,
    isNotasOwner,
    broadcastRecState,
    claimNotasOwnership,
    releaseNotasOwnership,
    setRecordingPresence,
    gravandoEmOutroDevice,
    presenceReady,
    myDeviceId: myDeviceId.current,
    myStableId: myStableId.current,
  }
}
